import {
  Button,
  DatePicker,
  Drawer,
  Flex,
  Form,
  Input,
  InputNumber,
  Popconfirm,
  PopconfirmProps,
  Row,
  Space,
  Table,
  TableColumnsType,
  Tag,
} from "antd";
import React, { useState } from "react";
import { TrancheDetailsModel } from "../../../domaine/projectModel";
import { GrStatusUnknown } from "react-icons/gr";
import dayjs from "dayjs";
import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteOutlined,
  EyeInvisibleOutlined,
  EyeOutlined,
  PlusOutlined,
  QuestionCircleOutlined,
} from "@ant-design/icons";
import { AddTrancheDrawer } from "./AddTrancheDrawer";
import { useSelector } from "react-redux";
import { RootState } from "../../../store/store";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import {
  deleteTranche,
  fetchProjectDetails,
} from "../../../store/slices/projectSlice";
import { fetchVisitesByTranche } from "../../../store/slices/visiteSlice";

interface Props {
  projectDetails: any;
  selectedTranche: number | null;
  setSelectedTranche: any;
}

export const TranchesTable: React.FC<Props> = ({
  projectDetails,
  selectedTranche,
  setSelectedTranche,
}) => {
  const dispatch = useDispatch();

  const confirm = async (id: number) => {
    await dispatch(deleteTranche(id))
    .unwrap()
    .then(async () => {
      // Get updated project details after deletion
      const updatedProject = await dispatch(fetchProjectDetails(projectDetails.id)).unwrap();
      toast.success("Tranche supprimée avec succés");
      
      // Handle selection after deletion
      if (selectedTranche === id) {
        // If the deleted tranche was selected
        if (updatedProject.tranches.length > 0) {
          // Select the first remaining tranche
          setSelectedTranche(updatedProject.tranches[0].id);
        } else {
          setSelectedTranche(null);
        }
      } else if (updatedProject.tranches.length === 1) {
        // If only one tranche remains, select it
        setSelectedTranche(updatedProject.tranches[0].id);
      }
    })
    .catch((error: any) => {
      console.error("Error deleting projet:", error);
      toast.error("Une erreur est survenue.");
    });
  };

  const cancel: PopconfirmProps["onCancel"] = (e) => {};

  // Define columns for the tranches table
  const tranchesColumns: TableColumnsType<TrancheDetailsModel> = [
    {
      title: "Nom",
      dataIndex: "name",
      key: "name",
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#42005a",
        },
      }),
      onCell: () => ({ style: { textAlign: "center" } }),
    },
    {
      title: "Surface foncière (m²)",
      dataIndex: "surface_fonciere",
      key: "surface_fonciere",
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#42005a",
        },
      }),
      onCell: () => ({ style: { textAlign: "center" } }),
    },

    {
      title: "Phase",
      dataIndex: "phase_du_projet",
      key: "phase_du_projet",
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#42005a",
        },
      }),
      onCell: () => ({ style: { textAlign: "center" } }),
      render: (_: any, record: any) => {
        const phase = record.phase_du_projet;

        if (!phase && !record.is_visited) {
          return <GrStatusUnknown />;
        }
        return phase;
      },
    },
    {
      title: "État d'avancement",
      dataIndex: "etat_avancement",
      key: "etat_avancement",
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#42005a",
        },
      }),
      onCell: () => ({ style: { textAlign: "center" } }),
      render: (_: any, record: any) => {
        const phase = record.phase_du_projet;
        let etatAvancement = record.etat_avancement;

        if (!etatAvancement && !record.is_visited) {
          return <GrStatusUnknown />;
        } else if (phase === "en chantier") {
          return etatAvancement;
        } else {
          return "-";
        }
      },
    },
    {
      title: "Visité",
      dataIndex: "is_visited",
      key: "is_visited",
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#42005a",
        },
      }),
      onCell: () => ({ style: { textAlign: "center" } }),
      render: (visited: boolean) => (
        visited ? <CheckCircleOutlined style={{ color: "green" }} /> : <CloseCircleOutlined style={{ color: "red" }} />
        
        
      ),
    },
    {
      title: "Suivi initial",
      dataIndex: "suivi_initial",
      key: "suivi_initial",
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#42005a",
        },
      }),
      onCell: () => ({ style: { textAlign: "center" } }),
      render: (suivi_initial: boolean) => (
        suivi_initial ? <CheckCircleOutlined style={{ color: "green" }} /> : <CloseCircleOutlined style={{ color: "red" }} />
      ),
    },
    {
      title: "Action",
      key: "action",
      onHeaderCell: () => ({
        style: {
          textAlign: "center",
          color: "#fff",
          backgroundColor: "#42005a",
        },
      }),
      onCell: () => ({ style: { textAlign: "center" } }),
      render: (_: any, record: any) => (
        <Flex style={{ width: "100%" }} justify="center" align="center">
          <Button
            type={record.id === selectedTranche ? "primary" : "default"}
            icon={<EyeOutlined />}
            onClick={() =>
              setSelectedTranche(
                record.id === selectedTranche ? null : record.id
              )
            }
            style={{ marginRight: "5px" }}
          />
          <Popconfirm
            title="Supprimer cette tranche"
            description="Etes-vous sûr ?"
            onConfirm={() => confirm(record.id)}
            onCancel={cancel}
            okText="Oui"
            cancelText="Non"
            icon={<QuestionCircleOutlined style={{ color: "red" }} />}
          >
            <Button icon={<DeleteOutlined />} type="primary" danger />
          </Popconfirm>
        </Flex>
      ),
    },
  ];
  return (
    <>
      <Table
        dataSource={projectDetails.tranches.map((tranche: any) => ({
          ...tranche,
          key: tranche.id,
        }))}
        size="small"
        columns={tranchesColumns}
        pagination={false}
        style={{ marginBottom: "20px" }}
      />
    </>
  );
};
